import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { messagesApi } from '../lib/api'

export default function MessageBell() {
  const [unreadCount, setUnreadCount] = useState(0)
  const navigate = useNavigate()

  const [pollInterval, setPollInterval] = useState(30000)
  const rateLimitedRef = useRef(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Same "arrived since the last poll, not just already unread" distinction
  // NotificationBell uses for 'notification:new' -- without it, MessageToast
  // would pop up for whatever was already unread from a prior session on
  // every page load.
  const prevUnreadCountRef = useRef<number | null>(null)

  const fetchUnread = async () => {
    try {
      const res = await messagesApi.getUnreadCount()
      if (res.data.success) {
        const newCount = res.data.data.count
        if (prevUnreadCountRef.current !== null && newCount > prevUnreadCountRef.current) {
          messagesApi.getLatestUnread().then((latestRes) => {
            const latest = latestRes.data.success ? latestRes.data.data : null
            if (latest) window.dispatchEvent(new CustomEvent('message:new', { detail: latest }))
          }).catch(() => {
            // ignore -- the badge count above is still correct either way
          })
        }
        prevUnreadCountRef.current = newCount
        setUnreadCount(newCount)
        if (rateLimitedRef.current) {
          rateLimitedRef.current = false
          setPollInterval(30000)
        }
      }
    } catch (err: any) {
      if (err?.response?.status === 429) {
        rateLimitedRef.current = true
        setPollInterval((prev) => Math.min(prev * 2, 300000))
      }
    }
  }

  useEffect(() => {
    fetchUnread()
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(fetchUnread, pollInterval)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [pollInterval])

  return (
    <button
      type="button"
      onClick={() => navigate('/messages')}
      aria-label="Messages"
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <span className="mdi mdi-message-text-outline" style={{ fontSize: '1.5rem', color: 'var(--text-secondary)' }} />
      {unreadCount > 0 && (
        <span
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            background: '#E53E3E',
            color: 'white',
            fontSize: '0.625rem',
            fontWeight: 700,
            borderRadius: '9999px',
            minWidth: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
          }}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  )
}
